import { IMindmapObject, IMindmapArrow, SnapPointId } from "../../api/mindmap";

export const getSnapPoints = (obj: IMindmapObject): { id: SnapPointId; x: number; y: number }[] => {
    const cx = obj.position.x;
    const cy = obj.position.y;
    const hw = obj.width / 2;
    const hh = obj.height / 2;
    const angle = (obj.angle || 0) * Math.PI / 180;
    
    const rotate = (x: number, y: number) => {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const dx = x - cx;
        const dy = y - cy;
        return {
            x: cx + dx * cos - dy * sin,
            y: cy + dx * sin + dy * cos
        };
    };
    
    const points: { id: SnapPointId; x: number; y: number }[] = [
        { id: 'top', ...rotate(cx, cy - hh) },
        { id: 'bottom', ...rotate(cx, cy + hh) },
        { id: 'left', ...rotate(cx - hw, cy) },
        { id: 'right', ...rotate(cx + hw, cy) },
        { id: 'tl', ...rotate(cx - hw, cy - hh) },
        { id: 'tr', ...rotate(cx + hw, cy - hh) },
        { id: 'bl', ...rotate(cx - hw, cy + hh) },
        { id: 'br', ...rotate(cx + hw, cy + hh) },
    ];
    
    return points;
};

export const getSnapPointCoords = (obj: IMindmapObject, snapId: SnapPointId): { x: number; y: number } => {
    const points = getSnapPoints(obj);
    return points.find(p => p.id === snapId) || { x: obj.position.x, y: obj.position.y };
};

export const getSnapDirection = (snapId: SnapPointId): { dx: number; dy: number } => {
    switch (snapId) {
        case 'top': return { dx: 0, dy: -1 };
        case 'bottom': return { dx: 0, dy: 1 };
        case 'left': return { dx: -1, dy: 0 };
        case 'right': return { dx: 1, dy: 0 };
        case 'tl': return { dx: -1, dy: -1 };
        case 'tr': return { dx: 1, dy: -1 };
        case 'bl': return { dx: -1, dy: 1 };
        case 'br': return { dx: 1, dy: 1 };
    }
};

export const calculateSnapExtension = (
    obj: IMindmapObject,
    snapId: SnapPointId,
    snapCoords: { x: number; y: number },
    extensionMargin: number = 30
): { x: number; y: number } => {
    const center = { x: obj.position.x, y: obj.position.y };
    
    const dx = snapCoords.x - center.x;
    const dy = snapCoords.y - center.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    
    if (dist < 0.01) {
        const dir = getSnapDirection(snapId);
        const len = Math.sqrt(dir.dx * dir.dx + dir.dy * dir.dy);
        const normDx = dir.dx / len;
        const normDy = dir.dy / len;
        return {
            x: snapCoords.x + normDx * extensionMargin,
            y: snapCoords.y + normDy * extensionMargin
        };
    }
    
    const normDx = dx / dist;
    const normDy = dy / dist;
    
    return {
        x: snapCoords.x + normDx * extensionMargin,
        y: snapCoords.y + normDy * extensionMargin
    };
};

interface Rect {
    x1: number; y1: number; x2: number; y2: number;
}

const getObjectRect = (obj: IMindmapObject, padding: number = 0): Rect => {
    const cx = obj.position.x;
    const cy = obj.position.y;
    const hw = obj.width / 2;
    const hh = obj.height / 2;
    const angle = (obj.angle || 0) * Math.PI / 180;
    
    const corners = [
        { x: -hw, y: -hh },
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh }
    ];
    
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    
    const rotatedCorners = corners.map(c => ({
        x: cx + c.x * cos - c.y * sin,
        y: cy + c.x * sin + c.y * cos
    }));
    
    const xs = rotatedCorners.map(c => c.x);
    const ys = rotatedCorners.map(c => c.y);
    
    return {
        x1: Math.min(...xs) - padding,
        y1: Math.min(...ys) - padding,
        x2: Math.max(...xs) + padding,
        y2: Math.max(...ys) + padding
    };
};

const lineIntersectsRect = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect
): boolean => {
    const left = rect.x1;
    const right = rect.x2;
    const top = rect.y1;
    const bottom = rect.y2;

    if (x1 >= left && x1 <= right && y1 >= top && y1 <= bottom) return true;
    if (x2 >= left && x2 <= right && y2 >= top && y2 <= bottom) return true;

    const lineIntersectsLine = (
        ax1: number, ay1: number, ax2: number, ay2: number,
        bx1: number, by1: number, bx2: number, by2: number
    ): boolean => {
        const d = (ax2 - ax1) * (by2 - by1) - (ay2 - ay1) * (bx2 - bx1);
        if (Math.abs(d) < 0.0001) return false;
        const t = ((bx1 - ax1) * (by2 - by1) - (by1 - ay1) * (bx2 - bx1)) / d;
        const u = -((ax2 - ax1) * (by1 - ay1) - (ay2 - ay1) * (bx1 - ax1)) / d;
        return t >= 0 && t <= 1 && u >= 0 && u <= 1;
    };

    if (lineIntersectsLine(x1, y1, x2, y2, left, top, right, top)) return true;
    if (lineIntersectsLine(x1, y1, x2, y2, right, top, right, bottom)) return true;
    if (lineIntersectsLine(x1, y1, x2, y2, right, bottom, left, bottom)) return true;
    if (lineIntersectsLine(x1, y1, x2, y2, left, bottom, left, top)) return true;

    for (const t of [0.25, 0.5, 0.75]) {
        const px = x1 + (x2 - x1) * t;
        const py = y1 + (y2 - y1) * t;
        if (px >= left && px <= right && py >= top && py <= bottom) return true;
    }

    return false;
};

const pathIntersectsRect = (
    path: { x: number; y: number }[],
    rect: Rect
): boolean => {
    for (let i = 0; i < path.length - 1; i++) {
        if (lineIntersectsRect(path[i].x, path[i].y, path[i + 1].x, path[i + 1].y, rect)) {
            return true;
        }
    }
    return false;
};

const pathIntersectsAnyRect = (
    path: { x: number; y: number }[],
    rects: Rect[]
): boolean => {
    for (const rect of rects) {
        if (pathIntersectsRect(path, rect)) {
            return true;
        }
    }
    return false;
};

const pointInsideRect = (x: number, y: number, rect: Rect): boolean => {
    return x >= rect.x1 && x <= rect.x2 && y >= rect.y1 && y <= rect.y2;
};

const countRectCrossings = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect
): number => {
    const left = rect.x1;
    const right = rect.x2;
    const top = rect.y1;
    const bottom = rect.y2;
    
    let crossings = 0;

    const checkEdge = (
        bx1: number, by1: number, bx2: number, by2: number
    ): void => {
        const d = (x2 - x1) * (by2 - by1) - (y2 - y1) * (bx2 - bx1);
        if (Math.abs(d) < 0.0001) return;
        const t = ((bx1 - x1) * (by2 - by1) - (by1 - y1) * (bx2 - bx1)) / d;
        const u = -((x2 - x1) * (by1 - y1) - (y2 - y1) * (bx1 - x1)) / d;
        if (t > 0.001 && t < 0.999 && u >= 0 && u <= 1) {
            crossings++;
        }
    };

    checkEdge(left, top, right, top);
    checkEdge(right, top, right, bottom);
    checkEdge(right, bottom, left, bottom);
    checkEdge(left, bottom, left, top);

    return crossings;
};

const getFirstExitT = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect
): number | null => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    
    let minExitT: number | null = null;
    
    if (dx !== 0) {
        const tLeft = (rect.x1 - x1) / dx;
        const tRight = (rect.x2 - x1) / dx;
        
        if (tLeft > 0 && tLeft < 1) {
            const y = y1 + dy * tLeft;
            if (y >= rect.y1 && y <= rect.y2) {
                if (minExitT === null || tLeft < minExitT) minExitT = tLeft;
            }
        }
        if (tRight > 0 && tRight < 1) {
            const y = y1 + dy * tRight;
            if (y >= rect.y1 && y <= rect.y2) {
                if (minExitT === null || tRight < minExitT) minExitT = tRight;
            }
        }
    }
    
    if (dy !== 0) {
        const tTop = (rect.y1 - y1) / dy;
        const tBottom = (rect.y2 - y1) / dy;
        
        if (tTop > 0 && tTop < 1) {
            const x = x1 + dx * tTop;
            if (x >= rect.x1 && x <= rect.x2) {
                if (minExitT === null || tTop < minExitT) minExitT = tTop;
            }
        }
        if (tBottom > 0 && tBottom < 1) {
            const x = x1 + dx * tBottom;
            if (x >= rect.x1 && x <= rect.x2) {
                if (minExitT === null || tBottom < minExitT) minExitT = tBottom;
            }
        }
    }
    
    return minExitT;
};

const MAX_EXIT_DISTANCE = 12;

const segmentExitsImmediately = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect,
    allowLongTravel: boolean = false
): boolean => {
    if (!pointInsideRect(x1, y1, rect)) {
        return true;
    }
    
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    
    if (segmentLength < 0.01) {
        return true;
    }
    
    const exitT = getFirstExitT(x1, y1, x2, y2, rect);
    
    if (exitT === null) {
        return false;
    }
    
    const exitDistance = exitT * segmentLength;
    
    if (allowLongTravel || exitDistance <= MAX_EXIT_DISTANCE) {
        const afterExitT = exitT + 0.02;
        if (afterExitT < 1) {
            const testX = x1 + dx * afterExitT;
            const testY = y1 + dy * afterExitT;
            if (!pointInsideRect(testX, testY, rect)) {
                return true;
            }
        }
        if (allowLongTravel && exitT < 1) {
            return true;
        }
    }
    
    return false;
};

const segmentEntersFromOutside = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect,
    allowLongTravel: boolean = false
): boolean => {
    if (!pointInsideRect(x2, y2, rect)) {
        return true;
    }
    
    const dx = x1 - x2;
    const dy = y1 - y2;
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    
    if (segmentLength < 0.01) {
        return true;
    }
    
    const entryT = getFirstExitT(x2, y2, x1, y1, rect);
    
    if (entryT === null) {
        return false;
    }
    
    const entryDistance = entryT * segmentLength;
    
    if (allowLongTravel || entryDistance <= MAX_EXIT_DISTANCE) {
        return true;
    }
    
    return false;
};

const lineTraversesRect = (
    x1: number, y1: number,
    x2: number, y2: number,
    rect: Rect,
    startIsOnBoundary: boolean,
    endIsOnBoundary: boolean,
    allowLongTravel: boolean = false
): boolean => {
    const startInside = pointInsideRect(x1, y1, rect);
    const endInside = pointInsideRect(x2, y2, rect);
    const crossings = countRectCrossings(x1, y1, x2, y2, rect);
    
    if (startIsOnBoundary && startInside) {
        if (!segmentExitsImmediately(x1, y1, x2, y2, rect, allowLongTravel)) {
            return true;
        }
        if (crossings >= 2) return true;
        if (endInside && !endIsOnBoundary) return true;
        return false;
    }
    
    if (endIsOnBoundary && endInside) {
        if (!segmentEntersFromOutside(x1, y1, x2, y2, rect, allowLongTravel)) {
            return true;
        }
        if (crossings >= 2) return true;
        if (startInside && !startIsOnBoundary) return true;
        return false;
    }
    
    if (crossings >= 2) return true;
    if (startInside || endInside) return true;
    if (crossings >= 1) return true;
    
    return false;
};

const pathIntersectsAnyRectSmart = (
    path: { x: number; y: number }[],
    obstacleRects: Rect[],
    fromRect: Rect,
    toRect: Rect,
    allowLongTravelFromStart: boolean = true,
    allowLongTravelToEnd: boolean = true
): boolean => {
    for (let i = 0; i < path.length - 1; i++) {
        const p1 = path[i];
        const p2 = path[i + 1];
        const isFirstSegment = i === 0;
        const isLastSegment = i === path.length - 2;
        
        for (const rect of obstacleRects) {
            if (lineIntersectsRect(p1.x, p1.y, p2.x, p2.y, rect)) {
                return true;
            }
        }
        
        if (lineTraversesRect(p1.x, p1.y, p2.x, p2.y, fromRect, isFirstSegment, false, isFirstSegment && allowLongTravelFromStart)) {
            return true;
        }
        
        if (lineTraversesRect(p1.x, p1.y, p2.x, p2.y, toRect, false, isLastSegment, isLastSegment && allowLongTravelToEnd)) {
            return true;
        }
    }
    return false;
};

const findFirstIntersectingRect = (
    path: { x: number; y: number }[],
    rects: Rect[]
): Rect | null => {
    for (const rect of rects) {
        if (pathIntersectsRect(path, rect)) {
            return rect;
        }
    }
    return null;
};

const createOrthogonalPath = (
    start: { x: number; y: number },
    end: { x: number; y: number },
    startDir: { dx: number; dy: number },
    endDir: { dx: number; dy: number },
    margin: number
): { x: number; y: number }[] => {
    const startExt = {
        x: start.x + startDir.dx * margin,
        y: start.y + startDir.dy * margin
    };
    const endExt = {
        x: end.x + endDir.dx * margin,
        y: end.y + endDir.dy * margin
    };

    if (startDir.dx !== 0 && endDir.dx !== 0) {
        const midX = (startExt.x + endExt.x) / 2;
        return [start, startExt, { x: midX, y: startExt.y }, { x: midX, y: endExt.y }, endExt, end];
    }
    if (startDir.dy !== 0 && endDir.dy !== 0) {
        const midY = (startExt.y + endExt.y) / 2;
        return [start, startExt, { x: startExt.x, y: midY }, { x: endExt.x, y: midY }, endExt, end];
    }
    if (startDir.dx !== 0 && endDir.dy !== 0) {
        return [start, startExt, { x: startExt.x, y: endExt.y }, endExt, end];
    }
    if (startDir.dy !== 0 && endDir.dx !== 0) {
        return [start, startExt, { x: endExt.x, y: startExt.y }, endExt, end];
    }
    return [start, startExt, endExt, end];
};

const createDetourAroundRect = (
    start: { x: number; y: number },
    end: { x: number; y: number },
    obstacle: Rect,
    margin: number
): { x: number; y: number }[][] => {
    const pad = margin;
    const corners = {
        tl: { x: obstacle.x1 - pad, y: obstacle.y1 - pad },
        tr: { x: obstacle.x2 + pad, y: obstacle.y1 - pad },
        bl: { x: obstacle.x1 - pad, y: obstacle.y2 + pad },
        br: { x: obstacle.x2 + pad, y: obstacle.y2 + pad }
    };

    const routes: { x: number; y: number }[][] = [];

    routes.push([start, { x: start.x, y: corners.tl.y }, corners.tl, { x: corners.tl.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.tr.y }, corners.tr, { x: corners.tr.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.bl.y }, corners.bl, { x: corners.bl.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.br.y }, corners.br, { x: corners.br.x, y: end.y }, end]);

    routes.push([start, { x: corners.tl.x, y: start.y }, corners.tl, { x: end.x, y: corners.tl.y }, end]);
    routes.push([start, { x: corners.tr.x, y: start.y }, corners.tr, { x: end.x, y: corners.tr.y }, end]);
    routes.push([start, { x: corners.bl.x, y: start.y }, corners.bl, { x: end.x, y: corners.bl.y }, end]);
    routes.push([start, { x: corners.br.x, y: start.y }, corners.br, { x: end.x, y: corners.br.y }, end]);

    routes.push([start, { x: start.x, y: corners.tl.y }, corners.tl, corners.tr, { x: corners.tr.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.tr.y }, corners.tr, corners.tl, { x: corners.tl.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.bl.y }, corners.bl, corners.br, { x: corners.br.x, y: end.y }, end]);
    routes.push([start, { x: start.x, y: corners.br.y }, corners.br, corners.bl, { x: corners.bl.x, y: end.y }, end]);
    routes.push([start, { x: corners.tl.x, y: start.y }, corners.tl, corners.bl, { x: end.x, y: corners.bl.y }, end]);
    routes.push([start, { x: corners.bl.x, y: start.y }, corners.bl, corners.tl, { x: end.x, y: corners.tl.y }, end]);
    routes.push([start, { x: corners.tr.x, y: start.y }, corners.tr, corners.br, { x: end.x, y: corners.br.y }, end]);
    routes.push([start, { x: corners.br.x, y: start.y }, corners.br, corners.tr, { x: end.x, y: corners.tr.y }, end]);

    return routes;
};

const pathLength = (path: { x: number; y: number }[]): number => {
    let len = 0;
    for (let i = 0; i < path.length - 1; i++) {
        len += Math.hypot(path[i + 1].x - path[i].x, path[i + 1].y - path[i].y);
    }
    return len;
};

const normalizePath = (path: { x: number; y: number }[]): { x: number; y: number }[] => {
    if (path.length <= 2) return path;
    
    const result: { x: number; y: number }[] = [path[0]];
    
    for (let i = 1; i < path.length - 1; i++) {
        const prev = result[result.length - 1];
        const curr = path[i];
        const next = path[i + 1];
        
        if (Math.abs(prev.x - curr.x) < 0.1 && Math.abs(prev.y - curr.y) < 0.1) {
            continue;
        }
        
        const isCollinear = (
            (Math.abs(prev.x - curr.x) < 0.1 && Math.abs(curr.x - next.x) < 0.1) ||
            (Math.abs(prev.y - curr.y) < 0.1 && Math.abs(curr.y - next.y) < 0.1)
        );
        
        if (isCollinear) {
            continue;
        }
        
        result.push(curr);
    }
    
    const lastPoint = path[path.length - 1];
    const prevPoint = result[result.length - 1];
    if (Math.abs(prevPoint.x - lastPoint.x) >= 0.1 || Math.abs(prevPoint.y - lastPoint.y) >= 0.1) {
        result.push(lastPoint);
    }
    
    return result;
};

export const calculateAutoPathWithSnaps = (
    start: { x: number; y: number },
    end: { x: number; y: number },
    fromSnap: SnapPointId,
    toSnap: SnapPointId,
    allObjects?: IMindmapObject[],
    fromIdx?: number,
    toIdx?: number
): { x: number; y: number }[] | null => {
    const margin = 40;
    
    const fromObj = (allObjects && fromIdx !== undefined && fromIdx >= 0) ? allObjects[fromIdx] : undefined;
    const toObj = (allObjects && toIdx !== undefined && toIdx >= 0) ? allObjects[toIdx] : undefined;
    
    const fromDir = getSnapDirection(fromSnap);
    const toDir = getSnapDirection(toSnap);

    if (!allObjects || fromIdx === undefined || toIdx === undefined) {
        return createOrthogonalPath(start, end, fromDir, toDir, margin);
    }
    
    if (!fromObj || !toObj) {
        return createOrthogonalPath(start, end, fromDir, toDir, margin);
    }

    const fromRect = getObjectRect(fromObj, 5);
    const toRect = getObjectRect(toObj, 5);

    const allRects = allObjects
        .filter((obj): obj is IMindmapObject => obj !== undefined)
        .map((obj, i) => ({ rect: getObjectRect(obj, 15), idx: i }));
    const obstacleRects = allRects.filter(r => r.idx !== fromIdx && r.idx !== toIdx).map(r => r.rect);

    const segmentClear = (p1: {x: number; y: number}, p2: {x: number; y: number}, rects: Rect[]) => {
        for (const rect of rects) {
            if (lineIntersectsRect(p1.x, p1.y, p2.x, p2.y, rect)) return false;
        }
        return true;
    };

    const safeStartExt = (dir: { dx: number; dy: number }) => {
        const ext = { x: start.x + dir.dx * margin, y: start.y + dir.dy * margin };
        if (segmentClear(start, ext, obstacleRects)) {
            return ext;
        }
        return null;
    };

    const safeEndExt = (dir: { dx: number; dy: number }) => {
        const ext = { x: end.x + dir.dx * margin, y: end.y + dir.dy * margin };
        if (segmentClear(end, ext, obstacleRects)) {
            return ext;
        }
        return null;
    };

    const trySnapDirection = (baseDir: {dx: number; dy: number}, startPt: {x: number; y: number}, safeFn: (d: {dx: number; dy: number}) => {x: number; y: number} | null): {x: number; y: number} | null => {
        const ext = safeFn(baseDir);
        if (ext) return ext;
        
        for (const extMargin of [60, 80, 100, 150, 200, 300]) {
            const testExt = { x: startPt.x + baseDir.dx * extMargin, y: startPt.y + baseDir.dy * extMargin };
            if (segmentClear(startPt, testExt, obstacleRects)) {
                return testExt;
            }
        }
        
        return null;
    };

    const rayStartExt = fromObj ? calculateSnapExtension(fromObj, fromSnap, start, margin) : { x: start.x + fromDir.dx * margin, y: start.y + fromDir.dy * margin };
    const rayEndExt = toObj ? calculateSnapExtension(toObj, toSnap, end, margin) : { x: end.x + toDir.dx * margin, y: end.y + toDir.dy * margin };
    
    const startExtResult = segmentClear(start, rayStartExt, obstacleRects) ? rayStartExt : trySnapDirection(fromDir, start, safeStartExt);
    const endExtResult = segmentClear(end, rayEndExt, obstacleRects) ? rayEndExt : trySnapDirection(toDir, end, safeEndExt);
    
    const startExtBlocked = !startExtResult;
    const endExtBlocked = !endExtResult;
    
    const findClearExtension = (origin: { x: number; y: number }, dir: { dx: number; dy: number }, blockedRects: Rect[]): { ext: { x: number; y: number }; isClear: boolean } => {
        for (const m of [20, 30, 40, 60, 80, 100, 150, 200, 300, 500, 800, 1200, 2000, 5000]) {
            const ext = { x: origin.x + dir.dx * m, y: origin.y + dir.dy * m };
            if (segmentClear(origin, ext, blockedRects)) {
                return { ext, isClear: true };
            }
        }
        
        const fallbackExt = { x: origin.x + dir.dx * 30, y: origin.y + dir.dy * 30 };
        return { ext: fallbackExt, isClear: false };
    };
    
    const allObjRectsForBounds = allObjects.map(obj => getObjectRect(obj, 20));
    const preBounds = {
        minX: Math.min(...allObjRectsForBounds.map(r => r.x1), start.x, end.x) - 80,
        maxX: Math.max(...allObjRectsForBounds.map(r => r.x2), start.x, end.x) + 80,
        minY: Math.min(...allObjRectsForBounds.map(r => r.y1), start.y, end.y) - 80,
        maxY: Math.max(...allObjRectsForBounds.map(r => r.y2), start.y, end.y) + 80
    };
    
    const startExtComputed = startExtResult ? { ext: startExtResult, isClear: true } : findClearExtension(start, fromDir, obstacleRects);
    const endExtComputed = endExtResult ? { ext: endExtResult, isClear: true } : findClearExtension(end, toDir, obstacleRects);
    
    const startExt = startExtComputed.ext;
    const endExt = endExtComputed.ext;
    const extensionsBlocked = !startExtComputed.isClear || !endExtComputed.isClear;

    const tryFindPath = (
        currentStart: { x: number; y: number },
        currentEnd: { x: number; y: number },
        rects: Rect[],
        depth: number
    ): { x: number; y: number }[] | null => {
        if (depth > 5) return null;

        const dx = currentEnd.x - currentStart.x;
        const dy = currentEnd.y - currentStart.y;
        
        let orthogonalPath: { x: number; y: number }[];
        if (Math.abs(dx) <= 1 || Math.abs(dy) <= 1) {
            orthogonalPath = [currentStart, currentEnd];
        } else if (Math.abs(dx) > Math.abs(dy)) {
            const midX = currentStart.x + dx / 2;
            orthogonalPath = [currentStart, { x: midX, y: currentStart.y }, { x: midX, y: currentEnd.y }, currentEnd];
        } else {
            const midY = currentStart.y + dy / 2;
            orthogonalPath = [currentStart, { x: currentStart.x, y: midY }, { x: currentEnd.x, y: midY }, currentEnd];
        }
        
        if (!pathIntersectsAnyRect(orthogonalPath, rects)) {
            return orthogonalPath;
        }

        const blocker = findFirstIntersectingRect(orthogonalPath, rects);
        if (!blocker) return orthogonalPath;

        const detours = createDetourAroundRect(currentStart, currentEnd, blocker, 25);
        
        let bestPath: { x: number; y: number }[] | null = null;
        let bestLen = Infinity;

        for (const detour of detours) {
            if (!pathIntersectsAnyRect(detour, rects)) {
                const len = pathLength(detour);
                if (len < bestLen) {
                    bestLen = len;
                    bestPath = detour;
                }
            }
        }

        if (bestPath) return bestPath;

        for (const detour of detours) {
            const stillBlocking = findFirstIntersectingRect(detour, rects);
            if (stillBlocking && stillBlocking !== blocker) {
                const subPath = tryFindPath(currentStart, currentEnd, rects.filter(r => r !== blocker), depth + 1);
                if (subPath && !pathIntersectsAnyRect(subPath, rects)) {
                    return subPath;
                }
            }
        }

        return null;
    };

    const allCollisionRects = [...obstacleRects, fromRect, toRect];
    
    const buildPath = (core: { x: number; y: number }[]): { x: number; y: number }[] => {
        if (core.length === 0) {
            const dx = endExt.x - startExt.x;
            const dy = endExt.y - startExt.y;
            if (Math.abs(dx) > 1 && Math.abs(dy) > 1) {
                if (Math.abs(dx) > Math.abs(dy)) {
                    const midX = startExt.x + dx / 2;
                    return normalizePath([start, startExt, { x: midX, y: startExt.y }, { x: midX, y: endExt.y }, endExt, end]);
                } else {
                    const midY = startExt.y + dy / 2;
                    return normalizePath([start, startExt, { x: startExt.x, y: midY }, { x: endExt.x, y: midY }, endExt, end]);
                }
            }
        }
        return normalizePath([start, startExt, ...core, endExt, end]);
    };
    
    const basicPath = buildPath([]);

    if (!pathIntersectsAnyRectSmart(basicPath, obstacleRects, fromRect, toRect)) {
        return basicPath;
    }

    const middlePath = tryFindPath(startExt, endExt, obstacleRects, 0);
    if (middlePath && middlePath.length >= 2) {
        const core = middlePath.slice(1, -1);
        const combined = buildPath(core);
        if (!pathIntersectsAnyRectSmart(combined, obstacleRects, fromRect, toRect)) {
            return combined;
        }
    }
    
    const fullMiddlePath = tryFindPath(startExt, endExt, allCollisionRects, 0);
    if (fullMiddlePath && fullMiddlePath.length >= 2) {
        const core = fullMiddlePath.slice(1, -1);
        const combined = buildPath(core);
        if (!pathIntersectsAnyRectSmart(combined, obstacleRects, fromRect, toRect)) {
            return combined;
        }
    }

    const allObjRects = allObjects.map(obj => getObjectRect(obj, 20));
    const bounds = {
        minX: Math.min(...allObjRects.map(r => r.x1), start.x, end.x) - 80,
        maxX: Math.max(...allObjRects.map(r => r.x2), start.x, end.x) + 80,
        minY: Math.min(...allObjRects.map(r => r.y1), start.y, end.y) - 80,
        maxY: Math.max(...allObjRects.map(r => r.y2), start.y, end.y) + 80
    };

    const extremeRoutes: { x: number; y: number }[][] = [];
    
    if (!startExtBlocked && !endExtBlocked) {
        extremeRoutes.push(
            [start, startExt, { x: startExt.x, y: bounds.minY }, { x: endExt.x, y: bounds.minY }, endExt, end],
            [start, startExt, { x: startExt.x, y: bounds.maxY }, { x: endExt.x, y: bounds.maxY }, endExt, end],
            [start, startExt, { x: bounds.minX, y: startExt.y }, { x: bounds.minX, y: endExt.y }, endExt, end],
            [start, startExt, { x: bounds.maxX, y: startExt.y }, { x: bounds.maxX, y: endExt.y }, endExt, end],
        );
    }
    
    extremeRoutes.push(
        [start, startExt, { x: startExt.x, y: bounds.minY }, { x: endExt.x, y: bounds.minY }, endExt, end],
        [start, startExt, { x: startExt.x, y: bounds.maxY }, { x: endExt.x, y: bounds.maxY }, endExt, end],
        [start, startExt, { x: bounds.minX, y: startExt.y }, { x: bounds.minX, y: endExt.y }, endExt, end],
        [start, startExt, { x: bounds.maxX, y: startExt.y }, { x: bounds.maxX, y: endExt.y }, endExt, end],
    );

    let bestRoute: { x: number; y: number }[] | null = null;
    let bestLen = Infinity;

    for (const route of extremeRoutes) {
        if (!pathIntersectsAnyRectSmart(route, obstacleRects, fromRect, toRect)) {
            const len = pathLength(route);
            if (len < bestLen) {
                bestLen = len;
                bestRoute = route;
            }
        }
    }

    if (bestRoute) {
        return normalizePath(bestRoute);
    }

    const midY = (startExt.y + endExt.y) / 2;
    const midlinePath = [start, startExt, { x: startExt.x, y: midY }, { x: endExt.x, y: midY }, endExt, end];
    if (!pathIntersectsAnyRectSmart(midlinePath, obstacleRects, fromRect, toRect)) {
        return normalizePath(midlinePath);
    }
    
    const midX = (startExt.x + endExt.x) / 2;
    const midlinePathX = [start, startExt, { x: midX, y: startExt.y }, { x: midX, y: endExt.y }, endExt, end];
    if (!pathIntersectsAnyRectSmart(midlinePathX, obstacleRects, fromRect, toRect)) {
        return normalizePath(midlinePathX);
    }

    for (const yOffset of [-150, 150, -250, 250]) {
        const farPath = [start, startExt, { x: startExt.x, y: startExt.y + yOffset }, { x: endExt.x, y: startExt.y + yOffset }, endExt, end];
        if (!pathIntersectsAnyRectSmart(farPath, obstacleRects, fromRect, toRect)) {
            return normalizePath(farPath);
        }
    }
    
    for (const xOffset of [-150, 150, -250, 250]) {
        const farPath = [start, startExt, { x: startExt.x + xOffset, y: startExt.y }, { x: startExt.x + xOffset, y: endExt.y }, endExt, end];
        if (!pathIntersectsAnyRectSmart(farPath, obstacleRects, fromRect, toRect)) {
            return normalizePath(farPath);
        }
    }

    for (const offset of [400, 600, 800, 1200]) {
        const veryFarPaths = [
            [start, startExt, { x: startExt.x, y: bounds.minY - offset }, { x: endExt.x, y: bounds.minY - offset }, endExt, end],
            [start, startExt, { x: startExt.x, y: bounds.maxY + offset }, { x: endExt.x, y: bounds.maxY + offset }, endExt, end],
            [start, startExt, { x: bounds.minX - offset, y: startExt.y }, { x: bounds.minX - offset, y: endExt.y }, endExt, end],
            [start, startExt, { x: bounds.maxX + offset, y: startExt.y }, { x: bounds.maxX + offset, y: endExt.y }, endExt, end],
        ];
        for (const farPath of veryFarPaths) {
            if (!pathIntersectsAnyRectSmart(farPath, obstacleRects, fromRect, toRect)) {
                return normalizePath(farPath);
            }
        }
    }

    const orthogonalFallbackPath = [start, startExt, { x: (startExt.x + endExt.x) / 2, y: startExt.y }, { x: (startExt.x + endExt.x) / 2, y: endExt.y }, endExt, end];
    if (!pathIntersectsAnyRectSmart(orthogonalFallbackPath, obstacleRects, fromRect, toRect)) {
        return normalizePath(orthogonalFallbackPath);
    }
    
    const orthogonalFallbackPathY = [start, startExt, { x: startExt.x, y: (startExt.y + endExt.y) / 2 }, { x: endExt.x, y: (startExt.y + endExt.y) / 2 }, endExt, end];
    if (!pathIntersectsAnyRectSmart(orthogonalFallbackPathY, obstacleRects, fromRect, toRect)) {
        return normalizePath(orthogonalFallbackPathY);
    }
    
    for (const offset of [2000, 3000, 5000, 10000]) {
        const emergencyPaths = [
            [start, startExt, { x: startExt.x, y: bounds.minY - offset }, { x: endExt.x, y: bounds.minY - offset }, endExt, end],
            [start, startExt, { x: startExt.x, y: bounds.maxY + offset }, { x: endExt.x, y: bounds.maxY + offset }, endExt, end],
            [start, startExt, { x: bounds.minX - offset, y: startExt.y }, { x: bounds.minX - offset, y: endExt.y }, endExt, end],
            [start, startExt, { x: bounds.maxX + offset, y: startExt.y }, { x: bounds.maxX + offset, y: endExt.y }, endExt, end],
        ];
        for (const path of emergencyPaths) {
            if (!pathIntersectsAnyRectSmart(path, obstacleRects, fromRect, toRect)) {
                return normalizePath(path);
            }
        }
    }
    
    for (const ultraOffset of [50000, 100000, 500000]) {
        const ultraFarPaths = [
            [start, startExt, { x: startExt.x, y: -ultraOffset }, { x: endExt.x, y: -ultraOffset }, endExt, end],
            [start, startExt, { x: startExt.x, y: ultraOffset }, { x: endExt.x, y: ultraOffset }, endExt, end],
            [start, startExt, { x: -ultraOffset, y: startExt.y }, { x: -ultraOffset, y: endExt.y }, endExt, end],
            [start, startExt, { x: ultraOffset, y: startExt.y }, { x: ultraOffset, y: endExt.y }, endExt, end],
        ];
        for (const path of ultraFarPaths) {
            if (!pathIntersectsAnyRectSmart(path, obstacleRects, fromRect, toRect)) {
                return normalizePath(path);
            }
        }
    }
    
    const simpleOrthogonalPaths: { x: number; y: number }[][] = [];
    
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        const midX = (start.x + end.x) / 2;
        const midY = (start.y + end.y) / 2;
        
        simpleOrthogonalPaths.push([start, { x: start.x, y: end.y }, end]);
        simpleOrthogonalPaths.push([start, { x: end.x, y: start.y }, end]);
        
        simpleOrthogonalPaths.push([start, { x: midX, y: start.y }, { x: midX, y: end.y }, end]);
        simpleOrthogonalPaths.push([start, { x: start.x, y: midY }, { x: end.x, y: midY }, end]);
        
        for (const offset of [50, 100, 150, 200]) {
            simpleOrthogonalPaths.push([start, { x: start.x, y: start.y - offset }, { x: end.x, y: start.y - offset }, end]);
            simpleOrthogonalPaths.push([start, { x: start.x, y: start.y + offset }, { x: end.x, y: start.y + offset }, end]);
            simpleOrthogonalPaths.push([start, { x: start.x - offset, y: start.y }, { x: start.x - offset, y: end.y }, end]);
            simpleOrthogonalPaths.push([start, { x: start.x + offset, y: start.y }, { x: start.x + offset, y: end.y }, end]);
        }
    }
    
    for (const path of simpleOrthogonalPaths) {
        if (!pathIntersectsAnyRectSmart(path, obstacleRects, fromRect, toRect)) {
            return normalizePath(path);
        }
    }
    
    const globalBounds = {
        minX: Math.min(...allObjects.map(o => o.position.x - o.width / 2 - 30)),
        maxX: Math.max(...allObjects.map(o => o.position.x + o.width / 2 + 30)),
        minY: Math.min(...allObjects.map(o => o.position.y - o.height / 2 - 30)),
        maxY: Math.max(...allObjects.map(o => o.position.y + o.height / 2 + 30))
    };
    
    const globalBypassPaths: { x: number; y: number }[][] = [
        [start, { x: start.x, y: globalBounds.minY - 50 }, { x: end.x, y: globalBounds.minY - 50 }, end],
        [start, { x: start.x, y: globalBounds.maxY + 50 }, { x: end.x, y: globalBounds.maxY + 50 }, end],
        [start, { x: globalBounds.minX - 50, y: start.y }, { x: globalBounds.minX - 50, y: end.y }, end],
        [start, { x: globalBounds.maxX + 50, y: start.y }, { x: globalBounds.maxX + 50, y: end.y }, end],
        
        [start, { x: start.x, y: globalBounds.minY - 100 }, { x: end.x, y: globalBounds.minY - 100 }, end],
        [start, { x: start.x, y: globalBounds.maxY + 100 }, { x: end.x, y: globalBounds.maxY + 100 }, end],
        [start, { x: globalBounds.minX - 100, y: start.y }, { x: globalBounds.minX - 100, y: end.y }, end],
        [start, { x: globalBounds.maxX + 100, y: start.y }, { x: globalBounds.maxX + 100, y: end.y }, end]
    ];
    
    for (const path of globalBypassPaths) {
        if (!pathIntersectsAnyRectSmart(path, obstacleRects, fromRect, toRect)) {
            return normalizePath(path);
        }
    }
    
    const cornerBypassPaths: { x: number; y: number }[][] = [];
    for (const offset of [80, 150, 250]) {
        cornerBypassPaths.push(
            [start, { x: globalBounds.minX - offset, y: start.y }, { x: globalBounds.minX - offset, y: globalBounds.minY - offset }, { x: end.x, y: globalBounds.minY - offset }, end],
            [start, { x: globalBounds.maxX + offset, y: start.y }, { x: globalBounds.maxX + offset, y: globalBounds.minY - offset }, { x: end.x, y: globalBounds.minY - offset }, end],
            [start, { x: globalBounds.minX - offset, y: start.y }, { x: globalBounds.minX - offset, y: globalBounds.maxY + offset }, { x: end.x, y: globalBounds.maxY + offset }, end],
            [start, { x: globalBounds.maxX + offset, y: start.y }, { x: globalBounds.maxX + offset, y: globalBounds.maxY + offset }, { x: end.x, y: globalBounds.maxY + offset }, end],
            
            [start, { x: start.x, y: globalBounds.minY - offset }, { x: globalBounds.minX - offset, y: globalBounds.minY - offset }, { x: globalBounds.minX - offset, y: end.y }, end],
            [start, { x: start.x, y: globalBounds.maxY + offset }, { x: globalBounds.minX - offset, y: globalBounds.maxY + offset }, { x: globalBounds.minX - offset, y: end.y }, end],
            [start, { x: start.x, y: globalBounds.minY - offset }, { x: globalBounds.maxX + offset, y: globalBounds.minY - offset }, { x: globalBounds.maxX + offset, y: end.y }, end],
            [start, { x: start.x, y: globalBounds.maxY + offset }, { x: globalBounds.maxX + offset, y: globalBounds.maxY + offset }, { x: globalBounds.maxX + offset, y: end.y }, end]
        );
    }
    
    for (const path of cornerBypassPaths) {
        if (!pathIntersectsAnyRectSmart(path, obstacleRects, fromRect, toRect)) {
            return normalizePath(path);
        }
    }
    
    return null;
};

export const calculateAutoPath = (
    start: { x: number; y: number },
    end: { x: number; y: number }
): { x: number; y: number }[] => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
        return [start, end];
    }
    
    if (Math.abs(dx) > Math.abs(dy)) {
        const midX = start.x + dx / 2;
        return [start, { x: midX, y: start.y }, { x: midX, y: end.y }, end];
    } else {
        const midY = start.y + dy / 2;
        return [start, { x: start.x, y: midY }, { x: end.x, y: midY }, end];
    }
};

export const pathToSvg = (points: { x: number; y: number }[]): string => {
    if (points.length < 2) return '';
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
        d += ` L ${points[i].x} ${points[i].y}`;
    }
    return d;
};
